import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { store } from "./store";

// Define a configuration type for each component that can be hydrated.
type ComponentConfig = {
  import: () => Promise<{ default: React.ComponentType<any> }>;
  type: "standard" | "routed";
  paths?: string[]; // Only for 'routed' components
};

type ComponentMap = {
  [key: string]: ComponentConfig;
};

// Map component names (from `data-hydrate-component` attribute) to their import paths.
const componentMap: ComponentMap = {
  ProductsContainer: {
    import: () =>
      import(
        "../templates/ecommerce/product/components/productsArchive/defaultArchive/ProductsContainer"
      ),
    type: "standard",
  },
  LanguageSwitcher: {
    import: () =>
      import("../parts/header/language/components/LanguageSwitcher"),
    type: "standard",
  },
  SingleProductContainer: {
    import: () =>
      import(
        "../templates/ecommerce/product/components/productSingle/SingleProductContainer"
      ),
    type: "routed",
    // Update these paths to match your Turkish WordPress permalinks.
    // I've included the most common ones based on your screenshot.
    paths: [
      "/urun/:productSlug", // Default: /urun/sample-product/
      "/urunler/:productSlug", // Shop base: /urunler/sample-product/
      "/urunler/:categorySlug/:productSlug", // Shop base with category
      // It's also good practice to keep the English defaults for robustness
      "/product/:productSlug",
      "/shop/:productSlug",
    ],
  },
  CollectionsContainer: {
    import: () =>
      import(
        "../templates/ecommerce/product/components/productsArchive/collectionsArchive/CollectionsContainer"
      ),
    type: "standard",
  },
};

/**
 * Finds all elements with the 'data-hydrate-component' attribute and renders
 * the corresponding React component into them.
 */
export const hydrateComponents = (): void => {
  const containers = document.querySelectorAll<HTMLElement>(
    "[data-hydrate-component]"
  );

  if (containers.length === 0) {
    return;
  }

  console.log(`[Hydrate] Found ${containers.length} component(s) to hydrate.`);

  containers.forEach((container) => {
    const componentName = container.dataset.hydrateComponent;
    const componentConfig = componentName ? componentMap[componentName] : null;

    if (componentConfig) {
      componentConfig
        .import()
        .then((module) => {
          const Component = module.default;
          const root = createRoot(container);
          let componentToRender;

          if (componentConfig.type === "routed" && componentConfig.paths) {
            componentToRender = (
              <Routes>
                {componentConfig.paths.map((path) => (
                  <Route key={path} path={path} element={<Component />} />
                ))}
              </Routes>
            );
          } else {
            componentToRender = <Component />;
          }

          const finalRender = (
            <React.StrictMode>
              <Provider store={store}>
                <BrowserRouter>{componentToRender}</BrowserRouter>
              </Provider>
            </React.StrictMode>
          );

          root.render(finalRender);
          console.log(
            `[Hydrate] Component "${componentName}" hydrated successfully.`
          );
        })
        .catch((err) => {
          console.error(
            `[Hydrate] Failed to load or render component "${componentName}":`,
            err
          );
          try {
            const root = createRoot(container);
            root.render(
              <div
                style={{
                  padding: "1rem",
                  border: "1px solid red",
                  color: "red",
                }}
              >
                Error loading component: {componentName}.
              </div>
            );
          } catch (renderError) {
            container.innerHTML = `Error loading component: ${componentName}.`;
          }
        });
    } else {
      console.warn(
        `[Hydrate] Component "${componentName}" not found in the component map.`
      );
    }
  });
};
